/**
 * OIDC flow orchestration — mirrors Espo's stock Backbone client.
 *
 * Step sequence (per Espo v9.3.6):
 * 1. GET /api/v1/Oidc/authorizationData → {clientId, endpoint, redirectUri, scopes, prompt, ...}
 * 2. Generate random state + nonce client-side
 * 3. Open popup window, navigate it to Google's authorization endpoint with state+nonce
 * 4. Poll popup.location until Google redirects back to /oauth-callback.php?code=...&state=...
 * 5. Verify state matches, extract code, close popup
 * 6. Return { code, nonce } to caller (which then POSTs to /api/v1/App/user)
 */

export interface OidcAuthorizationData {
  clientId: string;
  endpoint: string;
  redirectUri: string;
  scopes: string[];
  claims: string | null;
  prompt: "none" | "login" | "consent" | "select_account";
  maxAge: number | null;
}

export interface OidcCodeResult {
  code: string;
  nonce: string;
}

export class OidcError extends Error {
  constructor(
    public readonly kind:
      | "popup_blocked"
      | "state_mismatch"
      | "user_closed"
      | "provider_error"
      | "timeout"
      | "parse_error",
    message: string,
    public readonly providerCode?: string,
  ) {
    super(message);
    this.name = "OidcError";
  }
}

function randomToken(): string {
  // Stock Espo: (Math.random() + 1).toString(36).substring(4)
  // Strengthen to crypto.getRandomValues for entropy.
  const arr = new Uint8Array(16);
  crypto.getRandomValues(arr);
  return Array.from(arr, (b) => b.toString(16).padStart(2, "0")).join("");
}

export interface ParsedCallback {
  code?: string;
  state?: string;
  error?: string;
  errorDescription?: string;
}

function parseCallbackUrl(url: string): ParsedCallback | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  const q = parsed.searchParams;
  return {
    code: q.get("code") ?? undefined,
    state: q.get("state") ?? undefined,
    error: q.get("error") ?? undefined,
    errorDescription: q.get("error_description") ?? undefined,
  };
}

function buildAuthUrl(data: OidcAuthorizationData, state: string, nonce: string): string {
  const params: Record<string, string> = {
    client_id: data.clientId,
    redirect_uri: data.redirectUri,
    response_type: "code",
    scope: data.scopes.join(" "),
    state,
    nonce,
    prompt: data.prompt,
  };
  if (data.maxAge != null) params.max_age = String(data.maxAge);
  if (data.claims) params.claims = data.claims;

  const qs = Object.entries(params)
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join("&");
  return `${data.endpoint}?${qs}`;
}

/**
 * Open OIDC popup and wait for callback.
 * IMPORTANT: must be called from a direct user interaction (button click)
 * to avoid popup blockers.
 */
export function runOidcFlow(data: OidcAuthorizationData): Promise<OidcCodeResult> {
  return new Promise((resolve, reject) => {
    const popup = window.open(
      "about:blank",
      "kormaxOidc",
      "width=500,height=650,scrollbars=yes,resizable=yes",
    );
    if (!popup) {
      reject(new OidcError("popup_blocked", "Браузер заблокував спливаюче вікно входу. Дозвольте popup для crm.kormaxtrade.com.ua і спробуйте ще раз."));
      return;
    }

    const state = randomToken();
    const nonce = randomToken();
    popup.location.href = buildAuthUrl(data, state, nonce);

    // Hard cap: 5 min for user to complete Google auth.
    const TIMEOUT_MS = 5 * 60 * 1000;
    const POLL_MS = 250;
    const startedAt = Date.now();

    const cleanup = () => {
      window.clearInterval(interval);
      if (!popup.closed) popup.close();
    };

    const interval = window.setInterval(() => {
      if (popup.closed) {
        window.clearInterval(interval);
        reject(new OidcError("user_closed", "Вікно входу закрито до завершення."));
        return;
      }

      if (Date.now() - startedAt > TIMEOUT_MS) {
        cleanup();
        reject(new OidcError("timeout", "Час очікування входу через Google вичерпано. Спробуйте ще раз."));
        return;
      }

      let href: string;
      try {
        href = popup.location.href;
      } catch {
        // Cross-origin (still on accounts.google.com) — keep polling.
        return;
      }

      // Skip blank/intermediate states.
      if (!href || href === "about:blank") return;

      const cb = parseCallbackUrl(href);
      if (!cb) {
        cleanup();
        reject(new OidcError("parse_error", "Не вдалося розібрати відповідь від Google."));
        return;
      }

      // Wait until either code or error arrives.
      if (!cb.code && !cb.error) return;

      if (cb.state !== state) {
        cleanup();
        reject(new OidcError("state_mismatch", "Невідповідність state у відповіді. Спробуйте увійти ще раз."));
        return;
      }

      if (cb.error) {
        cleanup();
        reject(
          new OidcError(
            "provider_error",
            cb.errorDescription || "Google повернув помилку входу.",
            cb.error,
          ),
        );
        return;
      }

      if (cb.code) {
        cleanup();
        resolve({ code: cb.code, nonce });
      }
    }, POLL_MS);
  });
}

/* ─── LOGIN-1 (TZ v1.3 §2/§4): mobile full-page redirect path ─────────────────
 * Desktop keeps the popup (runOidcFlow above). On mobile, window.opener is null
 * after window.open, so the code never returns to the opener → login loop. Instead
 * we generate + persist state/nonce and navigate the whole tab to Google; on return
 * (forwarded by the custom oauth-callback EntryPoint to /login?code=&state=) LoginPage
 * reads the callback, verifies state, and completes via the SAME App/user path.
 * sessionStorage is per-tab + per-origin and survives the Google round-trip in-tab.
 */

const OIDC_PENDING_KEY = "kormax-oidc-pending";

export interface OidcPending {
  state: string;
  nonce: string;
}

/** Read the {state,nonce} stashed before a full-page redirect, or null if absent/corrupt. */
export function readPendingOidc(): OidcPending | null {
  try {
    const raw = sessionStorage.getItem(OIDC_PENDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<OidcPending>;
    if (typeof parsed?.state === "string" && typeof parsed?.nonce === "string") {
      return { state: parsed.state, nonce: parsed.nonce };
    }
    return null;
  } catch {
    return null;
  }
}

/** Drop the pending OIDC stash (after completion, error, or CSRF failure). */
export function clearPendingOidc(): void {
  try {
    sessionStorage.removeItem(OIDC_PENDING_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Start the MOBILE full-page OIDC redirect. Generates Web-Crypto state+nonce (same
 * randomToken as the popup path — no Math.random), persists them, then navigates the
 * whole tab to Google. Does not return (the page unloads). Throws synchronously only
 * if sessionStorage is unavailable — the caller surfaces an error and the password
 * form stays usable (no lockout).
 */
export function startOidcRedirect(data: OidcAuthorizationData): void {
  const state = randomToken();
  const nonce = randomToken();
  sessionStorage.setItem(OIDC_PENDING_KEY, JSON.stringify({ state, nonce } satisfies OidcPending));
  window.location.href = buildAuthUrl(data, state, nonce);
}

/** Parse the current page URL for an OIDC callback (mobile redirect return on /login). */
export function parseCurrentCallback(): ParsedCallback | null {
  return parseCallbackUrl(window.location.href);
}
